/**
 * The cross-course seat projection (issue #465, part 1a).
 *
 * ONE authenticated read per seat serves both the seat's root page and its My
 * Courses page. Two lists would mean two orders, which is the divergence this
 * projection exists to remove.
 *
 * The two seats stay separate here exactly as they do on the wire: two closed
 * models, not one shape with role-specific fields made optional. A teaching
 * response has no `continuation` key and a learning response has no `attention`
 * key, so a component cannot render staff-only work into a learner view even by
 * mistake. Nothing in this file widens a server Literal.
 */
import { api } from "@/lib/api/client";

export type SeatPerspective = "teaching" | "learning";

/** #427's three words, read the same way: ready, empty, and unreadable. */
export type SeatSection<T> =
  | { state: "ready"; value: T }
  | { state: "empty" }
  | { state: "unavailable" };

/**
 * The term is the one section with no `unavailable` arm.
 *
 * A projection whose scope is unknown fails the request instead of returning a
 * shell, so a completed read that found no current term is `empty` — never null,
 * and never a failure.
 */
export type SeatTermSection = { state: "ready"; value: SeatTerm } | { state: "empty" };

export interface SeatTerm {
  id: string;
  name: string;
  /** Two values, not three. A closed term belongs to My Courses' finished-term path. */
  status: "active" | "upcoming";
  /**
   * NULLABLE, because `lms_semester.start_date` and `end_date` are: a term may
   * be named before its dates are known. Nothing on the seat pages renders
   * either one, and nothing may substitute a date it was not given.
   */
  starts_on: string | null;
  ends_on: string | null;
}

/**
 * The complete active-term scope of the course list. `term` remains Today's
 * focal context, so callers must not use it to label a list that spans this
 * set.
 */
export type SeatActiveTerms = SeatTerm[];

/**
 * The class summary, as the DATABASE clocked it.
 *
 * There is no observed start instant anywhere in this schema, so every surface
 * says "scheduled now" and never "started at".
 */
export interface SeatClock {
  session_id: string;
  starts_at: string;
  ends_at: string;
  institutional_status: "scheduled" | "held" | "cancelled";
  clock_phase: "upcoming" | "scheduled_now" | "status_pending";
  session_href: string;
  destination_state?: "ready" | "missing_url" | "invalid_configuration" | "not_applicable" | "unavailable";
  meeting_provider?: "zoom" | "google_meet" | "in_person" | "other" | null;
  /** Exact canonical server destination, present only for a ready destination. */
  meeting_url?: string | null;
  location?: string | null;
}

export type SeatClockSection = SeatSection<SeatClock>;

/**
 * Parts, not prose. The server states which facts hold and supplies their
 * numbers and instants; the client composes the sentence.
 */
export interface TeachingWaitingPart {
  kind:
    | "review_backlog"
    | "register_open"
    | "next_deadline"
    // The attention feed's other kinds. These carry no `count` -- the server
    // cannot prove one from a bounded page -- so their clauses print none.
    | "overdue"
    | "returned"
    | "absences";
  count: number | null;
  at: string | null;
  title: string | null;
  href: string | null;
}

export interface LearningWaitingPart {
  kind: "due" | "returned" | "feedback_released" | "receipt";
  count: number | null;
  at: string | null;
  title: string | null;
  href: string | null;
}

export type TeachingWaitingIncompleteKind = "reviews" | "overdue_or_returned" | "absences";

export type TeachingWaitingSection =
  | { state: "ready"; value: { sentence_parts: TeachingWaitingPart[] } }
  | { state: "empty" }
  | {
    state: "incomplete";
    reason: "bounded_window";
    kinds: TeachingWaitingIncompleteKind[];
  }
  | { state: "unavailable" };
export type LearningWaitingSection = SeatSection<{ sentence_parts: LearningWaitingPart[] }>;

interface SeatCourseBase {
  term_status?: "active" | "upcoming" | "closed" | null;
  course_id: string;
  code: string;
  title: string;
  title_ar: string | null;
  crn: string | null;
  schedule_text: string | null;
  /**
   * Required, and it rides EVERY row. A seat may span courses in different
   * zones, so there is no single frame zone to stamp once.
   */
  timezone: string;
  /** HTTPS only, or null. Null renders the generated signature. */
  image_url: string | null;
  href: string;
  clock: SeatClockSection;
  rung: string | null;
}

export interface TeachingSeatCourse extends SeatCourseBase {
  waiting: TeachingWaitingSection;
  enrolled_count: number;
}

export interface LearningSeatCourse extends SeatCourseBase {
  waiting: LearningWaitingSection;
  instructor_display: string | null;
}

export type SeatCourse = TeachingSeatCourse | LearningSeatCourse;

export interface TeachingSeatCourses {
  items: TeachingSeatCourse[];
  /** Names the rule that ordered them. The client re-orders nothing. */
  pressure_order: "rung";
}

export interface LearningSeatCourses {
  items: LearningSeatCourse[];
  pressure_order: "rung";
}

export type TeachingCoursesSection = SeatSection<TeachingSeatCourses>;
export type LearningCoursesSection = SeatSection<LearningSeatCourses>;

export interface TeachingSeat {
  kind: "teaching";
  /** The complete current course-set count, derived from the same list. */
  course_count: number | null;
  shell_base: "/instructor" | "/admin/teaching";
}

export interface LearningSeat {
  kind: "learning";
  student_id: string;
  course_count: number | null;
  shell_base: "/student";
}

/** Which course the act belongs to. A hero with no course is where this could mislead. */
export interface SeatActionCourse {
  id: string;
  code: string;
  title: string;
}

export interface TeachingPrimaryAction {
  id: "open_class" | "record_attendance" | "review_attempt" | "open_assignment";
  reason: "class_in_progress" | "register_open" | "oldest_review" | "next_class" | "next_deadline";
  label: string;
  href: string;
  course: SeatActionCourse;
}

export interface LearningPrimaryAction {
  id: "open_class" | "open_assignment";
  reason: "class_in_progress" | "returned_revision" | "due_unattempted" | "next_class" | "evidence";
  label: string;
  href: string;
  course: SeatActionCourse;
}

export interface SeatAttentionItem {
  repair_address?: `${string}:${string}` | null;
  kind: "review" | "attendance" | "overdue" | "returned" | "absences";
  record_id: string;
  course_id: string;
  course_code: string;
  label: string;
  condition: string;
  /** An `absences` row's recorded unexcused absences this term; null for every other kind. */
  count: number | null;
  occurred_at: string;
  href: string;
}

export interface SeatAttention {
  /** Oldest first, bounded at 50. */
  items: SeatAttentionItem[];
  /** Counted under the SAME bound, so no number is offered that the query did not finish. */
  total: number;
  total_is_capped: boolean;
}

export type SeatAttentionSection = SeatSection<SeatAttention>;

export interface SeatContinuationItem {
  course_id: string;
  course_code: string;
  assignment: {
    id: string;
    title: string;
    status: "published" | "closed";
    due_at: string | null;
    points: string | null;
  };
  learner_state: "unattempted" | "submitted" | "resubmitted" | "returned" | "graded";
  receipt: { attempt_number: number; submitted_at: string } | null;
  feedback: {
    outcome: "returned" | "graded" | "complete" | "missing";
    released_at: string;
    score: string | null;
    preview: string | null;
    preview_truncated: boolean;
  } | null;
  href: string;
}

export interface SeatContinuation {
  items: SeatContinuationItem[];
  total: number;
}

export type SeatContinuationSection = SeatSection<SeatContinuation>;

/**
 * `score` is deliberately absent from a released row: a cross-course list of
 * numbers is the second gradebook the ledger rules against.
 */
export interface SeatReleasedItem {
  submission_id?: string | null;
  course_id: string;
  course_code: string;
  assignment: { id: string; title: string };
  outcome: "returned" | "graded" | "complete" | "missing";
  released_at: string;
  attempt_number: number;
  href: string;
}

export interface SeatReleased {
  items: SeatReleasedItem[];
  total: number;
}

export type SeatReleasedSection = SeatSection<SeatReleased>;

export interface SeatProgressItem {
  course_id: string;
  course_code: string;
  attendance: {
    rate_pct: number | null;
    present: number;
    late: number;
    absent: number;
    excused: number;
  };
  provisional: {
    status: "current" | "unavailable" | "inconsistent" | "stale";
    /** An opaque digest of the source facts, not a row id. */
    basis_id: string | null;
    percentage: number | null;
    letter: string | null;
    advisory: boolean | null;
  };
  certified: {
    status: "certified" | "uncertified";
    letter: string | null;
    score: string | null;
    kind: "certification" | "correction" | null;
    certified_at: string | null;
  };
}

export type SeatProgressSection = SeatSection<{ items: SeatProgressItem[] }>;

export type HomeRanking = SeatSection<{ candidates: Array<{ id: string; eligible: boolean; blocked: boolean; read: "ready" | "empty" | "unavailable"; rank: number | null }>; lead: string | null }>;

interface SeatProjectionBase {
  projection_version: "workspace-today-v1";
  perspective: SeatPerspective;
  generated_at: string;
  /** Missing older projections stay unranked. Only the server names an offered row. */
  ranking?: HomeRanking;
  offers?: Record<string, string>;
  /** Every active term contributing current courses, in server order. */
  active_terms: SeatActiveTerms;
  /** Today's focal term or, only with no active work, one upcoming context. */
  term: SeatTermSection;
}

export interface TeachingSeatProjection extends SeatProjectionBase {
  perspective: "teaching";
  seat: TeachingSeat;
  primary_action: TeachingPrimaryAction | null;
  courses: TeachingCoursesSection;
  attention: SeatAttentionSection;
}

export interface LearningSeatProjection extends SeatProjectionBase {
  perspective: "learning";
  seat: LearningSeat;
  primary_action: LearningPrimaryAction | null;
  courses: LearningCoursesSection;
  continuation: SeatContinuationSection;
  released: SeatReleasedSection;
  progress: SeatProgressSection;
}

export type SeatProjection = TeachingSeatProjection | LearningSeatProjection;

export const seatWorkspaceApi = {
  getTeaching: () => api.get<TeachingSeatProjection>("/workspaces/teaching/today"),
  getLearning: () => api.get<LearningSeatProjection>("/workspaces/learning/today"),
};
